<template>
    <ion-page>
        <standard-header title="Packs" align="middle"/>
        <ion-content :fullscreen="true" v-if="shipment && shipment.id > 0">
            <pull-down-refresh :callback="refresh"/>
            <sub-title>
                {{ shipment.reference }}
            </sub-title>
            <ion-item color="tertiary">
                <ion-label>Checkers</ion-label>
                <p>{{ checkerNames }}</p>
            </ion-item>
            <ion-searchbar animated mode="ios" :placeholder="placeholder" v-model="boxSearch"/>
            <div v-if="shipment.is_list">
                <div v-for="line in shipment.lines" :key="line.id">
                    <ion-item v-if="matchesSearch(line)" detail button @click="packListLine(line)">
                        <ion-icon slot="start" color="success" v-show="line.packed_at" :icon="checkmarkCircle" />
                        <ion-label class="ion-text-wrap">
                            <h2>SKU: {{ line.sku.sku }} x {{ line.qty_checked }}</h2>
                            <p>
                                Item: {{ line.item.item_number }}
                                <span v-if="line.packing_instructions">{{ line.packing_instructions }}</span>
                            </p>
                        </ion-label>
                    </ion-item>
                </div>
            </div>
            <div v-else v-for="(pallets, split) in shipment.packing_summary.splits" :key="split">
                <ion-item>
                  <strong>{{ split }}</strong>
                </ion-item>
                <div v-for="(group, palletName) in pallets" :key="palletName">
                    <ion-list>
                        <ion-item v-if="shipment.is_pallets && Object.keys(pallets).length > 1">
                            <ion-label><strong>{{ palletName }}</strong></ion-label>
                        </ion-item>
                        <div v-for="item in group" :key="`${item.number}${item.split}`">
                        <ion-item
                                  v-if="matchesSearch(item)"
                                  :key="item"
                                  :detail="canPack(item)"
                                  :button="canPack(item)"
                                  @click="pack(item.boxes, item)"
                        >
                            <ion-icon slot="start" :color="item.has_instructions?'danger':'success'" :icon="list" />
                            <ion-icon slot="start" color="success" v-show="isBoxGroupPacked(item)" :icon="checkmarkCircle" />
                            <ion-label>
                                <h2 v-if="item.boxes.length > 1">Boxes {{ item.boxes[0].number }} - {{ item.boxes[item.boxes.length-1].number }}</h2>
                                <h2 v-else>Box {{ item.boxes[0].number }}</h2>
                            </ion-label>
                        </ion-item>
                        </div>
                    </ion-list>
                </div>
            </div>
        </ion-content>
        <ion-footer>
            <ion-button fill="outline" color="success" expand="block" @click="signoff()">
                Sign Off {{ shipment.reference }}
            </ion-button>
        </ion-footer>
    </ion-page>
</template>

<script>
import PullDownRefresh from "@/components/PullDownRefresh";
import SubTitle from "@/components/SubTitle";
import {
    IonContent,
    IonItem,
    IonLabel,
    IonList,
    IonPage,
    IonIcon,
    IonFooter,
    IonButton,
    IonSearchbar,
    alertController
} from '@ionic/vue';
import StandardHeader from "@/components/StandardHeader";
import EventBus from "@/EventBus";
import _ from 'lodash';
import {modalController} from '@ionic/vue';
import {checkmarkCircle, alertCircleOutline, list} from 'ionicons/icons';
import PackLineModal from "@/views/modules/packing/pages/pack/components/PackLineModal";
import PackListItemModal from "@/views/modules/packing/pages/pack/components/PackListItemModal";
export default {
    name: "ListItems",
    inject: ['$http', '$toast', '$validationAlert'],
    components: {
        StandardHeader,
        IonSearchbar,
        SubTitle, PullDownRefresh, IonContent, IonItem,
        IonLabel, IonList, IonPage, IonIcon, IonFooter, IonButton
    },
    computed: {
        placeholder() {
            if (this.shipment.is_list) {
                return 'Search SKUs, Item #';
            }
            return 'Search box #';
        },
        params() {
            return this.$route.params;
        },
        checkerNames() {
            return _.map(this.shipment.checkers, 'name').join(', ');
        }
    },
    mounted() {
        this.refresh();
    },
    methods: {
        matchesSearch(item) {
            if (this.boxSearch.length < 1) {
                return true;
            }

            if (this.shipment.is_list) {
                // in this case, "item" is actually a "line"
                // search differently for lists ....
                const itemNumberMatch = item?.item.item_number?.toLowerCase().indexOf(this.boxSearch.toLowerCase()) > -1;
                const skuMatch = item?.sku.sku?.toLowerCase().indexOf(this.boxSearch.toLowerCase()) > -1;
                const barcodeMatch = item.sku?.barcode?.toLowerCase()?.indexOf(this.boxSearch?.toLowerCase()) > -1;
                return itemNumberMatch || skuMatch || barcodeMatch;
            }



            return _.filter(item.boxes, box => {
                return box.number == this.boxSearch;
            }).length > 0;
        },
        canPack(pallet) {
            return (this.shipment.stage === 'processing' && pallet.is_fast_track_to_pack) || this.shipment.stage === 'pack';
        },
        isBoxGroupPacked(item) {
            return _.filter(item.boxes, box => {
                return !box.packed_at;
            }).length < 1;
        },
        async signoff() {
            alertController.create({
                header: 'Please Confirm',
                message: 'Are you sure this shipment is fully packed and ready to go?',
                buttons: [
                    'Cancel', {
                        text: 'Yes',
                        handler: () => {
                            EventBus().emitter.emit('loading', true);
                            this.$http.put(`/api/packs/shipments/${this.shipment.id}`, {
                                signed_off: true
                            }).then(() => {
                                this.$router.back();
                                this.$toast.success("Pack signed off");
                            }).catch(error => {
                                this.$validationAlert(error);
                            }).finally(() => {
                                EventBus().emitter.emit('loading', false);
                            });
                        }
                    }
                ]
            }).then(alert => alert.present());
        },
        packListLine(line) {
            modalController.create({
                component: PackListItemModal,
                componentProps: {
                    shipment: this.shipment,
                    line: line
                }
            }).then(modal => {
                modal.present();
                modal.onDidDismiss()
                    .then(response => {
                        if (response.data) {
                            this.$toast.success("Pack saved");
                            this.refresh();
                        }
                    });
            });
        },
        async pack(boxes, pallet) {

            if (!this.canPack(pallet)) {
                alertController.create({
                    header: 'Cannot pack',
                    message: 'This box is not ready to pack yet - awaiting processing',
                    buttons: ['Ok']
                }).then(a => a.present());
                return;
            }

            modalController.create({
                component: PackLineModal,
                componentProps: {
                    shipment: this.shipment,
                    boxes: boxes
                }
            }).then(modal => {
                modal.present();
                modal.onDidDismiss()
                    .then(response => {
                        if (response.data) {
                            this.$toast.success("Pack saved");
                            this.refresh();
                        }
                    });
            });
        },
        refresh(event = null) {
            EventBus().emitter.emit('loading', true);
            this.$http.get(`/api/packs/me/shipments/${this.params.id}`, {
                params: {include: 'lines,lines.item,lines.sku,packing_summary,checkers'}
            }).then(response => {
                this.shipment = response.data;
            }).finally(() => {
                if (event && event.target.complete) {
                    event.target.complete();
                }
                EventBus().emitter.emit('loading', false);
            });
        }
    },
    data() {
        return {
            boxSearch: '',
            shipment: {}
        }
    },
    setup() {
        return {
            checkmarkCircle, alertCircleOutline, list
        };
    }
}
</script>

<style scoped>

</style>
